import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Accordion,
  Button,
  Container,
  Dropdown,
  Form,
  Header,
  Icon,
  Segment,
} from "semantic-ui-react";
import { updateMe } from "../api/auth";
import { useAuth } from "../contexts/AuthContext";

const actionToIndex = [{ index: 0, action: "call-recording" }];

function Settings() {
  const { action } = useParams();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const _index = actionToIndex.filter((x) => x.action === action);
    setActiveIndex(_index.length > 0 ? _index[0].index : 0);
  }, [action]);

  const handleClick = (_, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newIndex);
  };

  return (
    <Container
      style={{
        margin: "5px 0px",
      }}
    >
      <Segment basic>
        <Header as="h2" style={{ marginBottom: "40px" }}>
          Manage Connect Settings
        </Header>
        <Accordion fluid styled>
          <AccordionItem
            index={0}
            activeIndex={activeIndex}
            handleClick={handleClick}
            title={
              <>
                <Icon name="dot circle outline" /> Call Recording
              </>
            }
          >
            <CallRecording />
          </AccordionItem>
          <AccordionItem
            index={1}
            activeIndex={activeIndex}
            handleClick={handleClick}
            title={
              <>
                <Icon name="handshake" /> CRM Settings
              </>
            }
          >
            <CrmSettings />
          </AccordionItem>
        </Accordion>
      </Segment>
    </Container>
  );
}

function AccordionItem({ index, activeIndex, handleClick, title, children }) {
  return (
    <>
      <Accordion.Title
        active={activeIndex === index}
        index={index}
        onClick={handleClick}
      >
        {title}
      </Accordion.Title>
      <Accordion.Content active={activeIndex === index}>
        {children}
      </Accordion.Content>
    </>
  );
}

function CallRecording() {
  const { apiUser, setApiUser } = useAuth();

  const [recordingOption, setRecordingOption] = useState(apiUser.record_calls);
  const [changed, setChanged] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setChanged(recordingOption !== apiUser.record_calls);
  }, [apiUser.record_calls, recordingOption]);

  const handleChangeRecordingOption = (_, data) => {
    setRecordingOption(data.value);
  };

  const handleSubmit = () => {
    setSaving(true);
    updateMe({
      record_calls: recordingOption,
    }).then((res) => {
      setSaving(false);
      setApiUser(res.data.data);
      toast("Call recording options updated", { type: "success" });
    });
  };

  return (
    <Container>
      <Segment basic>
        <Form onSubmit={handleSubmit}>
          <Form.Field>
            <label>Record calls</label>
            <p>
              iPECS records all calls by default. If you only want to record
              certain calls, you can choose that option here. This will only
              affect calls made in soft phone mode.
            </p>
            <p>
              Connect will automatically toggle off recording for the direction
              you don't want to record after the call has connected.
            </p>
            <Dropdown
              placeholder="All calls"
              fluid
              selection
              value={recordingOption}
              options={[
                { key: "ALL", value: "ALL", text: "All calls" },
                {
                  key: "INBOUND",
                  value: "INBOUND",
                  text: "Inbound calls only",
                },
              ]}
              onChange={handleChangeRecordingOption}
            />
          </Form.Field>

          <Button type="submit" loading={saving} disabled={!changed} primary>
            Save
          </Button>
        </Form>
      </Segment>
    </Container>
  );
}

function CrmSettings() {
  const { apiUser, setApiUser } = useAuth();

  const [phoneFormat, setPhoneFormat] = useState(apiUser.crm_phone_format);
  const [autoOpenCrmRecord, setAutoOpenCrmRecord] = useState(
    apiUser.auto_open_crm_record,
  );
  const [changed, setChanged] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setChanged(
      phoneFormat !== apiUser.crm_phone_format ||
        autoOpenCrmRecord !== apiUser.auto_open_crm_record,
    );
  }, [phoneFormat, autoOpenCrmRecord, apiUser, setChanged]);

  const handleChangeCrmPhoneFormat = (_, data) => {
    setPhoneFormat(data.value);
  };

  const handleChangeAutoOpenCrm = (_, data) => {
    setAutoOpenCrmRecord(data.value);
  };

  const handleSubmit = () => {
    setSaving(true);
    updateMe({
      crm_phone_format: phoneFormat,
      auto_open_crm_record: autoOpenCrmRecord,
    }).then((res) => {
      setSaving(false);
      setApiUser(res.data.data);
      toast("CRM settings updated", { type: "success" });
    });
  };

  return (
    <Container>
      <Segment basic>
        <Form onSubmit={handleSubmit}>
          <Form.Field>
            <label>Phone Number Format</label>
            <p>
              The format you are storing numbers in on the CRM system(s) that
              are integrated with Connect (using the Apps page).
            </p>
            <p>
              An example of international (E.164) format is +442030123123
              <br />
              An example of national format is 02030123123
            </p>

            <Dropdown
              placeholder="International (E.164)"
              fluid
              selection
              value={phoneFormat}
              options={[
                { key: "E164", value: "E164", text: "International (E.164)" },
                {
                  key: "NATIONAL",
                  value: "NATIONAL",
                  text: "National (no spaces)",
                },
                {
                  key: "NATIONAL_WITH_SPACES",
                  value: "NATIONAL_WITH_SPACES",
                  text: "National (with spaces)",
                },
                {
                  key: "INTERNATIONAL_WITH_SPACES",
                  value: "INTERNATIONAL_WITH_SPACES",
                  text: "International (with spaces)",
                },
              ]}
              onChange={handleChangeCrmPhoneFormat}
            />
          </Form.Field>

          <Form.Field>
            <label>
              Automatically open CRM records when calls are answered?
            </label>
            <p>
              When you receive an inbound call a button will be displayed which
              will link to your CRM contact record.
              <br />
              If this enabled the link will automatically be opened when you
              answer the call.
            </p>
            <Dropdown
              placeholder="No"
              fluid
              selection
              value={autoOpenCrmRecord}
              options={[
                { key: "Yes", value: true, text: "Yes" },
                { key: "No", value: false, text: "No" },
              ]}
              onChange={handleChangeAutoOpenCrm}
            />
          </Form.Field>

          <Button type="submit" loading={saving} disabled={!changed} primary>
            Save
          </Button>
        </Form>
      </Segment>
    </Container>
  );
}

export default Settings;
